import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { NAV_ITEMS, NAV_SERVICES, NAV_PRICING, NAV_ABOUT, NAV_LOCATIONS, cityLinksByCounty, activeNavItem } from "@/data/nav";

/**
 * Desktop main menu (SEO audit batch 2, Part 3): five items, four of them with
 * dropdowns. Every dropdown link is a real <a href> that is ALWAYS in the DOM —
 * and therefore in the prerendered HTML of every page — and is only hidden with
 * CSS while closed (visibility/opacity), so crawlers can follow the links to
 * every service and city page. Nothing is mounted on click.
 *
 * Opening: pointer hover (150 ms intent delay), click on the chevron button,
 * or keyboard focus on the item. Closing: pointer leaves (150 ms), Esc (focus
 * returns to the item), focus leaving the item, a click outside, or a route
 * change. The item's word is a plain link to its hub page.
 */
const HOVER_DELAY_MS = 150;

const HEADING = "font-condensed text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500";
const PANEL_LINK = "block rounded-md px-2 py-1.5 text-sm text-[#0E1F3A] transition-colors hover:bg-blue-50 hover:text-[#1D4ED8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#1D4ED8]";
const PANEL_STRONG = "inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-semibold text-[#1D4ED8] transition-colors hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#1D4ED8]";

const PanelLink = ({ to, label, testId, strong = false }) => (
  <li>
    <Link to={to} className={strong ? PANEL_STRONG : PANEL_LINK} data-testid={testId}>
      {label}
      {strong && <span aria-hidden="true">→</span>}
    </Link>
  </li>
);

const ServicesPanel = () => (
  <div className="w-[34rem] p-5" data-testid="nav-panel-services">
    <div className="grid grid-cols-2 gap-6">
      {[NAV_SERVICES.homeowners, NAV_SERVICES.builders].map((col) => (
        <div key={col.heading}>
          <p className={HEADING}>{col.heading}</p>
          <ul className="mt-2 space-y-0.5">
            {col.links.map((l) => (
              <PanelLink key={l.to} to={l.to} label={l.label} testId={`nav-services-${l.to.split("/").pop()}`} />
            ))}
          </ul>
        </div>
      ))}
    </div>
    <ul className="mt-3 border-t border-border pt-3">
      <PanelLink to={NAV_SERVICES.all.to} label={NAV_SERVICES.all.label} testId="nav-services-all" strong />
    </ul>
  </div>
);

const LocationsPanel = () => (
  <div className="w-[52rem] p-5" data-testid="nav-panel-locations">
    <div className="grid grid-cols-[1.6fr_1fr_1fr_1.1fr] gap-6">
      {NAV_LOCATIONS.counties.map((county) => (
        <div key={county.id}>
          <p className={HEADING}>
            <Link to={county.to} className="rounded-sm hover:text-[#1D4ED8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1D4ED8]" data-testid={`nav-county-${county.id}`}>
              {county.name}
            </Link>
          </p>
          <ul className={`mt-2 space-y-0.5 ${county.id === "king" ? "columns-2 gap-x-3 [&>li]:break-inside-avoid" : ""}`}>
            {cityLinksByCounty(county.id).map((c) => (
              <PanelLink key={c.key} to={c.to} label={c.label} testId={c.testId} />
            ))}
          </ul>
        </div>
      ))}
      <div>
        <p className={HEADING}>Also serving</p>
        <ul className="mt-2 space-y-0.5">
          {NAV_LOCATIONS.alsoServing.map((a) => (
            <PanelLink key={a.key} to={a.to} label={a.label} testId={`nav-area-${a.key}`} />
          ))}
        </ul>
        <ul className="mt-3 border-t border-border pt-3">
          <PanelLink to={NAV_LOCATIONS.all.to} label={NAV_LOCATIONS.all.label} testId="nav-locations-all" strong />
        </ul>
      </div>
    </div>
  </div>
);

const SimplePanel = ({ links, testId, prefix }) => (
  <ul className="w-56 space-y-0.5 p-3" data-testid={testId}>
    {links.map((l) => (
      <PanelLink key={l.to} to={l.to} label={l.label} testId={`${prefix}-${l.to.replace(/\//g, "") || "home"}`} />
    ))}
  </ul>
);

const PANELS = {
  services: { Component: ServicesPanel, align: "left" },
  locations: { Component: LocationsPanel, align: "center" },
  pricing: { Component: () => <SimplePanel links={NAV_PRICING} testId="nav-panel-pricing" prefix="nav-pricing" />, align: "left" },
  about: { Component: () => <SimplePanel links={NAV_ABOUT} testId="nav-panel-about" prefix="nav-about" />, align: "left" },
};

const wordClass = (active) =>
  `nav-underline rounded-sm px-1 py-2 text-[13px] font-medium transition-colors hover:text-[#1D4ED8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D4ED8] xl:text-sm ${
    active ? "text-[#1D4ED8]" : "text-[#0E1F3A]"
  }`;

const NavDropdownItem = ({ item, active, open, onOpen, onClose, onToggle }) => {
  const ref = useRef(null);
  const wordRef = useRef(null);
  const panelId = useId();
  const timer = useRef(null);
  const { Component, align } = PANELS[item.id];

  const clearTimer = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };
  const schedule = (fn) => {
    clearTimer();
    timer.current = window.setTimeout(fn, HOVER_DELAY_MS);
  };
  useEffect(() => clearTimer, []);

  const onKeyDown = (e) => {
    if (e.key === "Escape" && open) {
      e.preventDefault();
      onClose();
      wordRef.current?.focus();
    }
  };
  // Keyboard: focus ENTERING the item (from outside it) opens the panel; moving
  // focus within the item (e.g. word -> chevron after Esc) does not re-open it,
  // and focus leaving the item closes it.
  const onFocus = (e) => {
    if (!ref.current?.contains(e.relatedTarget)) onOpen();
  };
  const onBlur = (e) => {
    if (!ref.current?.contains(e.relatedTarget)) onClose();
  };

  return (
    <li
      ref={ref}
      className="relative"
      onMouseEnter={() => schedule(onOpen)}
      onMouseLeave={() => schedule(onClose)}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      data-testid={`nav-item-${item.id}`}
      data-open={open ? "true" : "false"}
    >
      <div className="flex items-center">
        <Link ref={wordRef} to={item.to} className={wordClass(active)} aria-current={active ? "true" : undefined} data-testid={`nav-${item.id}`}>
          {item.label}
        </Link>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={`${open ? "Close" : "Open"} ${item.label} menu`}
          className={`-ml-0.5 flex h-8 w-6 items-center justify-center rounded-sm transition-colors hover:text-[#1D4ED8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1D4ED8] ${active ? "text-[#1D4ED8]" : "text-slate-500"}`}
          data-testid={`nav-${item.id}-toggle`}
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      </div>
      {/* Always rendered; CSS-hidden while closed so every link is in the HTML. The
          transparent top strip bridges the gap between word and panel for the pointer. */}
      <div
        id={panelId}
        role="region"
        aria-label={`${item.label} menu`}
        className={`absolute top-full z-50 pt-2 transition-[opacity,transform] duration-150 ${
          align === "center" ? "left-1/2 -translate-x-1/2" : "left-0"
        } ${open ? "visible translate-y-0 opacity-100" : "invisible -translate-y-1 opacity-0"}`}
        data-testid={`nav-dropdown-${item.id}`}
        data-state={open ? "open" : "closed"}
      >
        <div className="rounded-xl border border-border bg-white/95 shadow-xl shadow-[#0A1628]/10 backdrop-blur-md">
          <Component />
        </div>
      </div>
    </li>
  );
};

export const MainNav = () => {
  const { pathname } = useLocation();
  const [openId, setOpenId] = useState(null);
  const navRef = useRef(null);
  const active = activeNavItem(pathname);

  useEffect(() => setOpenId(null), [pathname]);

  // Click outside closes whichever dropdown is open.
  useEffect(() => {
    if (!openId) return undefined;
    const onDown = (e) => {
      if (!navRef.current?.contains(e.target)) setOpenId(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openId]);

  const open = useCallback((id) => setOpenId(id), []);
  const close = useCallback((id) => setOpenId((cur) => (cur === id ? null : cur)), []);
  const toggle = useCallback((id) => setOpenId((cur) => (cur === id ? null : id)), []);

  return (
    <nav ref={navRef} className="hidden lg:block" aria-label="Main navigation" data-testid="main-nav">
      <ul className="flex items-center gap-2 xl:gap-4">
        {NAV_ITEMS.map((item) =>
          PANELS[item.id] ? (
            <NavDropdownItem
              key={item.id}
              item={item}
              active={active === item.id}
              open={openId === item.id}
              onOpen={() => open(item.id)}
              onClose={() => close(item.id)}
              onToggle={() => toggle(item.id)}
            />
          ) : (
            <li key={item.id} data-testid={`nav-item-${item.id}`}>
              <Link to={item.to} className={wordClass(active === item.id)} aria-current={active === item.id ? "true" : undefined} data-testid={`nav-${item.id}`}>
                {item.label}
              </Link>
            </li>
          )
        )}
      </ul>
    </nav>
  );
};
